import { useEffect, useRef, useState } from "react";
import { CheckCircle, ChevronDown, Shield, Zap, Recycle, FlaskConical, BarChart2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ANNEX_ITEMS = [
  {
    icon: Shield,
    color: "from-blue-500 to-blue-600",
    glow: "rgba(59,130,246,0.15)",
    category: "01 · Identity & Traceability",
    headline: "Who made it, where, and when",
    fields: [
      "Manufacturer name, address, contact",
      "Economic operators across the supply chain",
      "Battery model ID / serial number / batch",
      "Date of manufacture",
      "Country of origin",
    ],
  },
  {
    icon: FlaskConical,
    color: "from-blue-500 to-blue-600",
    glow: "rgba(59,130,246,0.15)",
    category: "02 · Materials & Substances",
    headline: "What's inside it",
    fields: [
      "Active cathode / anode chemistry",
      "Electrolyte composition",
      "Hazardous substances above threshold",
      "Cobalt, lithium, nickel: % by weight",
      "Cell-level materials declaration",
    ],
  },
  {
    icon: Zap,
    color: "from-blue-500 to-blue-600",
    glow: "rgba(59,130,246,0.15)",
    category: "03 · Carbon Footprint",
    headline: "The climate cost, per kWh",
    fields: [
      "Lifecycle carbon footprint (kg CO₂e/kWh)",
      "Breakdown: raw materials, cell mfg., assembly, logistics",
      "Carbon footprint declaration class (A–E)",
      "Third-party verification status",
      "Carbon intensity trend over time",
    ],
  },
  {
    icon: BarChart2,
    color: "from-blue-500 to-blue-600",
    glow: "rgba(59,130,246,0.15)",
    category: "04 · Due Diligence",
    headline: "Responsible sourcing evidence",
    fields: [
      "Supply chain due diligence policy",
      "Conflict-mineral risk assessment",
      "Third-party audit certificates",
      "Supplier grievance mechanisms",
      "Human rights & labour standards",
    ],
  },
  {
    icon: BarChart2,
    color: "from-blue-500 to-blue-600",
    glow: "rgba(59,130,246,0.15)",
    category: "05 · Performance & Durability",
    headline: "How it performs across its life",
    fields: [
      "Rated capacity (Ah) & energy (kWh)",
      "Cycle life & calendar life",
      "State of Health (SoH) - live & original",
      "Charge/discharge rates (C-rate)",
      "Operating temperature range",
    ],
  },
  {
    icon: Recycle,
    color: "from-blue-500 to-blue-600",
    glow: "rgba(59,130,246,0.15)",
    category: "06 · Circularity & Second Life",
    headline: "What happens at end of life",
    fields: [
      "Recycled content by material (%)",
      "Recyclability rate (% by weight)",
      "Disassembly instructions & time",
      "Second-life application suitability",
      "Approved recyclers & take-back info",
    ],
  },
  {
    icon: CheckCircle,
    color: "from-blue-500 to-blue-600",
    glow: "rgba(59,130,246,0.15)",
    category: "07 · Conformity & Certifications",
    headline: "Proof it meets the rules",
    fields: [
      "EU Declaration of Conformity",
      "CE marking documentation",
      "Test reports (IEC 62619, UN 38.3)",
      "QSeal (eIDAS) for EU registry",
      "Responsible economic operator (REO) ID",
    ],
  },
];

function AnnexCard({ item, index, visible, className = "" }) {
  const Icon = item.icon;
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`rounded-2xl border overflow-hidden cursor-pointer bg-white ${className}`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(30px)",
        transition: `opacity 0.6s ease ${index * 0.08}s, transform 0.6s ease ${index * 0.08}s, border-color 0.3s ease, box-shadow 0.3s ease`,
        borderColor: expanded ? "rgba(59,130,246,0.4)" : "#e2e8f0",
        boxShadow: expanded ? `0 4px 20px ${item.glow}` : "0 1px 3px rgba(0,0,0,0.06)",
      }}
      onClick={() => setExpanded(e => !e)}
    >
      <div className="flex items-start gap-4 p-5 h-[128px]">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${item.color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-mono text-slate-400 mb-0.5">{item.category}</div>
          <div className="font-semibold text-slate-900 text-lg">{item.headline}</div>
        </div>
        <ChevronDown
          className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1 transition-transform duration-300"
          style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </div>

      {/* Expanded fields */}
      <div
        className="overflow-hidden transition-all duration-500"
        style={{ maxHeight: expanded ? "320px" : "0px" }}
      >
        <div className="px-5 pb-5 border-t border-slate-100">
          <div className="pt-4 space-y-2">
            {item.fields.map(f => (
              <div key={f} className="flex items-start gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                <span className="text-slate-600 text-base">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WhyBatteriesFirst() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const ob = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (sectionRef.current) ob.observe(sectionRef.current);
    return () => ob.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-24 bg-white overflow-hidden"
    >
      {/* Subtle background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-slate-100" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-slate-100" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-blue-50 rounded-full blur-[120px] opacity-60" />
        <div className="absolute bottom-1/3 left-0 w-[400px] h-[400px] bg-blue-50 rounded-full blur-[120px] opacity-50" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Heading */}
        <div
          className="text-center mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 text-blue-600 text-xs font-semibold uppercase tracking-widest mb-6">
            EU Battery Regulation 2023/1542
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight mb-5">
            A battery's whole life,{" "}
            <span className="text-blue-600">
              on a single scan
            </span>
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Annex XIII of EU Regulation 2023/1542 mandates seven categories of data in
            every battery passport. From raw materials to recycling - every step must be
            traceable, verifiable, and machine-readable.
          </p>

          {/* Deadline badge */}
          <div
            className="inline-flex items-center gap-3 mt-8 px-6 py-3 rounded-2xl border"
            style={{
              background: "#eff6ff",
              borderColor: "#bfdbfe",
            }}
          >
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-blue-700 font-semibold text-sm">
              Mandatory from <strong>18 February 2027</strong> - all new EV, e-bike &amp; industrial batteries ≥ 2 kWh
            </span>
          </div>
        </div>

        {/* Annex XIII cards: 4 on the first row, 3 centred on the second (desktop) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 items-start">
          {ANNEX_ITEMS.map((item, i) => (
            <AnnexCard
              key={i}
              item={item}
              index={i}
              visible={visible}
              className={`lg:col-span-3 ${i === 4 ? "lg:col-start-2" : ""} ${
                i === 6 ? "sm:col-span-2 sm:max-w-md sm:mx-auto sm:w-full lg:max-w-none lg:mx-0" : ""
              }`}
            />
          ))}
        </div>

        {/* CTA */}
        <div className="flex justify-center mt-12">
          <button
            onClick={() => {
              navigate("/battery-passport");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="group flex items-center justify-center gap-3 px-8 py-4 rounded-xl text-sm font-bold text-white transition-all duration-300"
            style={{
              background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)",
              boxShadow: "0 4px 20px rgba(37,99,235,0.35)",
              opacity: visible ? 1 : 0,
              transition: "opacity 0.7s ease 0.7s, box-shadow 0.2s ease, transform 0.2s ease",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = "0 8px 30px rgba(37,99,235,0.55)";
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = "0 4px 20px rgba(37,99,235,0.35)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Explore Battery Passport
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
          </button>
        </div>
      </div>
    </section>
  );
}